import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TrackingApiService } from '../services/tracking-api.service';
import { parseApiDate, statusLabel } from '../tracking-format.util';
import type { AuditLogEntry } from '../tracking-demo.models';

/** Audit tab: paginated list of admin actions (view/watch/disconnect/blacklist/…). */
@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="space-y-4">
      <div class="overflow-x-auto rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">
        <table class="min-w-full text-sm">
          <thead class="bg-slate-50 dark:bg-white/5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-ink-soft/50">
            <tr>
              <th class="px-3 py-2 text-left font-medium">Time</th>
              <th class="px-3 py-2 text-left font-medium">Admin</th>
              <th class="px-3 py-2 text-left font-medium">Action</th>
              <th class="px-3 py-2 text-left font-medium">Target</th>
              <th class="px-3 py-2 text-left font-medium">Reason</th>
              <th class="px-3 py-2 text-left font-medium">Admin IP</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-line/60">
            @for (a of rows(); track a.id) {
              <tr class="hover:bg-slate-50 dark:hover:bg-line/40">
                <td class="px-3 py-2 tabular-nums text-gray-500">{{ timeOf(a.createdAt) }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">{{ a.adminName || a.adminId || '—' }}</td>
                <td class="px-3 py-2"><span class="rounded px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200">{{ label(a.action) }}</span></td>
                <td class="px-3 py-2 font-mono text-[11px] text-gray-500">{{ a.targetId || '—' }}</td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-300">{{ a.reason || '—' }}</td>
                <td class="px-3 py-2 font-mono text-[11px] text-gray-500">{{ a.adminIp || '—' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="6" class="px-3 py-10 text-center text-gray-400">{{ loading() ? 'Loading…' : 'No audit entries.' }}</td></tr>
            }
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between text-sm text-gray-500">
        <span>{{ total() }} entries</span>
        <div class="flex items-center gap-2">
          <button type="button" (click)="prev()" [disabled]="page() <= 1"
            class="rounded-md border border-gray-200 dark:border-white/10 px-2 py-1 disabled:opacity-40">Prev</button>
          <span>Page {{ page() }} / {{ totalPages() }}</span>
          <button type="button" (click)="next()" [disabled]="page() >= totalPages()"
            class="rounded-md border border-gray-200 dark:border-white/10 px-2 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  `,
})
export class AuditLogsComponent implements OnInit {
  private readonly api = inject(TrackingApiService);

  readonly rows = signal<AuditLogEntry[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSize = 25;
  readonly loading = signal(false);

  readonly label = statusLabel;

  /** Local date+time (backend stores naive UTC — pinned to UTC before formatting). */
  timeOf(iso?: string | null): string {
    const d = parseApiDate(iso);
    return d
      ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      : '—';
  }

  ngOnInit(): void { this.load(); }

  totalPages(): number { return Math.max(1, Math.ceil(this.total() / this.pageSize)); }
  prev(): void { if (this.page() > 1) { this.page.update((p) => p - 1); this.load(); } }
  next(): void { if (this.page() < this.totalPages()) { this.page.update((p) => p + 1); this.load(); } }

  async load(): Promise<void> {
    this.loading.set(true);
    try {
      const res = await this.api.getAuditLogs(this.page(), this.pageSize);
      this.rows.set(res.items || []);
      this.total.set(res.total || 0);
    } catch {
      /* handled by api */
    } finally {
      this.loading.set(false);
    }
  }
}
